import React, { createContext, useContext, useEffect, useLayoutEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useEffectOnce } from '@guryou/hooks';
import { DynamicThemeContext } from '@guryou/html-components';
import { stateIsLoading, stateIsNotInitialized, widgetGetIntegration } from '@guryou/redux';
import { ensureWidgetIntegrationGoogleFont } from '../../utils/widgetIntegrationGoogleFonts';

const WidgetAuthBrandingContext = createContext(null);

const WidgetAuthBrandingProvider = ({ children }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { integrationId } = useParams();
  const { setMainColor } = useContext(DynamicThemeContext);
  const integration = useSelector(state => state.widget.integration);

  useEffectOnce(() => {
    if (integrationId) {
      dispatch(widgetGetIntegration(integrationId));
    }
  });

  useEffect(() => {
    if ((stateIsNotInitialized(integration) || stateIsLoading(integration)) && !integration?.data) {
      return;
    }
    if (!integration.data) {
      return;
    }
    const isAuth = integration.data.integrationType === 'auth' || integration.data.configuration?.widgetMode === 'auth';
    if (!isAuth) {
      history.replace(`/website-integration/${integrationId}`);
    }
  }, [integration.data, integrationId, history, integration]);

  const { providerId, configuration, provider, name: integrationLabel } = integration.data || {};
  const hasOpenParam = new URLSearchParams(location.search).has('open');
  const primaryColor = configuration?.primaryColor;
  const authButtonStyles = configuration
    ? {
        loginBackground: configuration.authLoginBackground,
        loginText: configuration.authLoginText,
        loginBorder: configuration.authLoginBorder,
        loginBorderWidth: configuration.authLoginBorderWidth,
        registerBackground: configuration.authRegisterBackground,
        registerText: configuration.authRegisterText,
        registerBorder: configuration.authRegisterBorder,
        registerBorderWidth: configuration.authRegisterBorderWidth,
        borderRadius: configuration.authButtonBorderRadius,
        visibleButtons: configuration.authVisibleButtons || 'both',
      }
    : null;
  const fontF = configuration?.fontFamily ? configuration.fontFamily : 'Metropolis';
  const fontS = configuration?.fontStyle ? configuration.fontStyle : 'sans-serif';

  useEffect(() => {
    if (primaryColor) {
      setMainColor(primaryColor);
    }
  }, [primaryColor, setMainColor]);

  useLayoutEffect(() => {
    // Keep auth widget transparent even during integration loading phase.
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById('root');

    const prevHtmlBg = html.style.backgroundColor;
    const prevBodyBg = body.style.backgroundColor;
    const prevRootBg = root?.style.backgroundColor ?? '';

    html.style.backgroundColor = 'transparent';
    body.style.backgroundColor = 'transparent';
    if (root) {
      root.style.backgroundColor = 'transparent';
    }

    return () => {
      html.style.backgroundColor = prevHtmlBg;
      body.style.backgroundColor = prevBodyBg;
      if (root) {
        root.style.backgroundColor = prevRootBg;
      }
    };
  }, []);

  useEffect(() => {
    ensureWidgetIntegrationGoogleFont(fontF);
  }, [fontF]);

  const fontFamilyStyle = fontF && fontF !== 'Metropolis' ? { fontFamily: fontF } : {};
  let fontStyleCSS = {};
  if (fontS && fontS !== 'sans-serif') {
    if (fontS === 'italic') {
      fontStyleCSS = { fontStyle: 'italic' };
    } else if (fontS === 'bold' || fontS === 'Double-struck') {
      fontStyleCSS = { fontWeight: 'bold' };
    }
  }

  const logoUrl = provider?.images?.[0]?.medium || provider?.images?.[0]?.large || provider?.images?.[0]?.xLarge || null;

  const value = {
    accentColor: primaryColor || 'var(--feel-primary)',
    brandName: provider?.name || integrationLabel || '',
    logoUrl,
    fontF,
    fontS,
    authButtonStyles,
    integrationLoaded: !!(integration.data && providerId),
  };

  if ((stateIsNotInitialized(integration) || stateIsLoading(integration)) && !integration?.data) {
    // When arriving with ?open=..., avoid button skeleton flash before modal opens.
    if (hasOpenParam) {
      return <div className="w-full h-full min-h-[88px] bg-transparent" aria-busy="true" aria-label="Loading" />;
    }

    return (
      <div
        className="w-full h-full min-h-[88px] flex items-center justify-center box-border p-0 bg-transparent"
        aria-busy="true"
        aria-label="Loading"
      >
        <div className="w-full max-w-[360px] flex gap-2">
          <div className="h-10 flex-1 rounded-lg border border-gray-300/50 bg-gray-200/70 animate-pulse" />
          <div className="h-10 flex-1 rounded-lg border border-gray-300/50 bg-gray-200/50 animate-pulse" />
        </div>
      </div>
    );
  }

  if (!integration.data || !providerId) {
    return (
      <div className="w-full box-border p-4">
        <p className="text-sm text-gray-600 text-left">Integration not found or invalid.</p>
      </div>
    );
  }

  return (
    <div className="w-full min-h-0" style={{ ...fontFamilyStyle, ...fontStyleCSS }}>
      <WidgetAuthBrandingContext.Provider value={value}>{children}</WidgetAuthBrandingContext.Provider>
    </div>
  );
};

function useWidgetAuthBranding() {
  const ctx = useContext(WidgetAuthBrandingContext);
  return ctx;
}

export { WidgetAuthBrandingProvider, useWidgetAuthBranding };
